/**
 * Cell Filters Context
 *
 * Provides filter state to cell components without prop drilling.
 * Allows cells to opt-in to filter awareness as needed.
 *
 * @module CellFiltersContext
 */

import { createContext, useContext } from 'react';

// Import the Filters type from your types file
import type { Filters } from '@/hooks/useAssets';

/**
 * Context for providing filter state to cell components
 */
const CellFiltersContext = createContext<Filters | undefined>(undefined);

/**
 * Provider component for cell filter context
 */
export const CellFiltersProvider = CellFiltersContext.Provider;

/**
 * Hook to access filter state from cell components
 *
 * @returns Current filter state or undefined if not within provider
 *
 * @example
 * ```typescript
 * const filters = useCellFilters();
 * const activeOriginFilters = filters?.advancedFilters?.origin;
 * ```
 */
export const useCellFilters = () => {
  return useContext(CellFiltersContext);
};
